import React, { useState } from "react";
import { Star } from "lucide-react";

/**
 * StarRating — 5-star row.
 * mode="display" renders read-only stars filled to `value` (supports halves).
 * mode="interactive" lets the user click a star to set a rating; calls
 *   `onChange(stars)`. Highlights on hover.
 */
export function StarRating({
  value = 0,
  count,
  onChange,
  size = 18,
  interactive = false,
  className = "",
  "data-testid": testid,
}) {
  const [hover, setHover] = useState(0);
  const active = interactive ? hover || value : value;

  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid={testid} role="radiogroup">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.floor(active);
        const half = !filled && n - 0.5 <= active;
        const color = filled || half ? "text-yellow-400" : "text-neutral-600";
        return interactive ? (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange?.(n)}
            className={`p-0.5 transition-transform hover:scale-110 ${color}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            data-testid={`${testid}-star-${n}`}
          >
            <Star size={size} strokeWidth={1.5} fill={filled ? "currentColor" : "none"} />
          </button>
        ) : (
          <span key={n} className={color}>
            <Star size={size} strokeWidth={1.5} fill={filled ? "currentColor" : "none"} />
          </span>
        );
      })}
      {typeof count === "number" && (
        <span className="ml-2 font-mono-label text-xs text-neutral-400">
          ({count.toLocaleString()} rating{count === 1 ? "" : "s"})
        </span>
      )}
    </div>
  );
}

export function formatCompact(n) {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1)}K`;
  return String(num);
}
